"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformRawSurveyConfig = exports.removeEmptyArrays = void 0;
const isPlainObject_1 = __importDefault(require("lodash/isPlainObject"));
const shuffle_1 = __importDefault(require("lodash/shuffle"));
const mapValues_1 = __importDefault(require("lodash/mapValues"));
const pickBy_1 = __importDefault(require("lodash/pickBy"));
// Обратные преобразования: https://github.yandex-team.ru/pythia/survey-api/blob/master/controllers/survey/config.js
// в protobuf нельзя отличить пустой массив от отсутствующего значения см. repeated
// Не удаляем пустые массивы из showIf.<...>.value и arguments,
// т.к. это может быть настоящий пустой массив, без которого ломается логика
function removeEmptyArrays(got, ignoreFields = ['value', 'arguments']) {
    function remove(got) {
        const notNull = x => x !== null;
        if (Array.isArray(got)) {
            if (got.length === 0) {
                return null;
            }
            return got.map(remove).filter(notNull);
        }
        if ((0, isPlainObject_1.default)(got)) {
            return Object.fromEntries(Object.entries(got)
                .map(([key, value]) => {
                if (ignoreFields.includes(key)) {
                    return [key, value];
                }
                return [key, remove(value)];
            })
                .filter(([, value]) => notNull(value)));
        }
        return got;
    }
    return remove(got);
}
exports.removeEmptyArrays = removeEmptyArrays;
function isNumeric(num) {
    return !isNaN(num);
}
// eslint-disable-next-line max-statements, complexity
function unflatCondition(condition) {
    // список узлов с id преращаем в древовидное условие
    const graphNodes = {};
    for (const node of condition) {
        const preparedNode = Object.assign({}, node);
        delete preparedNode.id;
        if (preparedNode.node) {
            const { key } = preparedNode.node;
            preparedNode.node = [preparedNode.node.type, isNumeric(key) ? Number(key) : key];
        }
        if (preparedNode.arguments) {
            preparedNode.value = preparedNode.arguments;
        }
        delete preparedNode.arguments;
        graphNodes[node.id || 0] = preparedNode;
    }
    for (const node of Object.values(graphNodes)) {
        if (node.content) {
            node.content = node.content.map(id => graphNodes[id]);
        }
    }
    return graphNodes[0];
}
// eslint-disable-next-line max-statements, complexity
function convertValueArrayToMap(arr) {
    // массив [{id:1 ...}, {id: 24, ...}, ...] приводим к объекту вида {"1" : {id:1 ...}, "24": {id: 24, ...}, ...}
    // если внутри есть усовия покза (в т.ч. условия показа вариантов ответов), то разворачиваем их в в дерево
    const result = {};
    if (Array.isArray(arr) && arr.length > 0) {
        for (const node of arr) {
            const newNode = Object.assign({}, node);
            // eslint-disable-next-line max-depth
            for (const option of newNode.options || []) {
                // eslint-disable-next-line max-depth
                if (option.showIf) {
                    option.showIf = unflatCondition(option.showIf);
                }
            }
            // eslint-disable-next-line max-depth
            if (newNode.showIf) {
                newNode.showIf = unflatCondition(newNode.showIf);
            }
            // eslint-disable-next-line max-depth
            if (newNode.condition) {
                newNode.condition = unflatCondition(newNode.condition);
            }
            result[String(node.id)] = newNode;
        }
    }
    return result;
}
function convertActionArrayToMap(conditions) {
    // массив [{"action": "skip", "condition": {id:1 ...}}, {"action": "finish", "condition": {id: 24, ...}}]
    // приводим к объекту {"skip" : {id:1 ...}, "finish": {id: 24, ...}, ...}
    // условия разворачиваем из списка в дерево
    const result = {};
    for (const { action, condition } of conditions) {
        result[action] = unflatCondition(condition);
    }
    return result;
}
function preparePageOrder(pageOrder) {
    return pageOrder.reduce((acc, item) => {
        if (item.rotation) {
            item.pages = (0, shuffle_1.default)(item.pages);
            if (item.sampling && isNumeric(item.sampling)) {
                item.pages = item.pages.slice(0, item.sampling);
            }
        }
        acc.push(...item.pages);
        return acc;
    }, []);
}
/**
 * Паста с survey-api (см. ручка start)
 * Принимает словарь entities, для каждого элемента из которого
 * при наличии флага rotation выполняет перемешивание содержимого поля
 * с названием, указанным в rotatableProp.
 * Возвращает словарь в котором каждому ключу соответствует список отротированных элементов (id и если существует, то type)
 */
function rotate(entities, rotatableProp) {
    const rotatable = (0, pickBy_1.default)(entities, e => e.rotation && e[rotatableProp] && e[rotatableProp].length > 1);
    // eslint-disable-next-line max-statements, complexity
    return (0, mapValues_1.default)(rotatable, q => {
        let firstWidget;
        let lastWidget;
        if (q.type === 'page') {
            // Временный (надеюсь) костыль для страниц
            // Если на всю страницу навешан флаг "ротация" и на ней в самом начале/в конце есть текстовые блоки
            // – то их не ротируем и оставляем на своих местах, считая что это как бы "заголовок" и "послесловие"
            const children = q[rotatableProp];
            if (children[0].type === 'widget') {
                firstWidget = children.shift();
            }
            if (children[children.length - 1].type === 'widget') {
                lastWidget = children.pop();
            }
        }
        q[rotatableProp] = (0, shuffle_1.default)(q[rotatableProp]);
        if (firstWidget) {
            q[rotatableProp].unshift(firstWidget);
        }
        if (lastWidget) {
            q[rotatableProp].push(lastWidget);
        }
        return q[rotatableProp].map(item => {
            const o = { id: item.id };
            if (item.type) {
                o.type = item.type;
            }
            return o;
        });
    });
}
// eslint-disable-next-line max-statements
function transformRawSurveyConfig(config) {
    const result = removeEmptyArrays(config);
    if (result.groups) {
        result.groups = convertValueArrayToMap(result.groups);
    }
    if (result.questions) {
        result.questions = convertValueArrayToMap(result.questions);
    }
    if (result.widgets) {
        result.widgets = convertValueArrayToMap(result.widgets);
    }
    if (result.conditions) {
        result.conditions = convertActionArrayToMap(result.conditions);
    }
    result.pageOrder = preparePageOrder(result.pageOrder);
    delete result.survey;
    // TODO: когда будет не только для директа, надо будет что-то придумывать
    result.redirects = {};
    // TODO: ответ этих методов надо не забыть передать в "новый" start
    result.questionOptions = rotate(result.questions, 'options');
    result.groupChildren = rotate(result.groups, 'children');
    return result;
}
exports.transformRawSurveyConfig = transformRawSurveyConfig;
